import type { ComputedFieldDefinition, FieldDefinition } from '@cloud/custom-modules'
import type { LayoutManifest } from '@cloud/modules'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { MoreHorizontal, Pencil } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { EntityDetail } from '@/components/custom/entity-detail'
import { type LayoutFieldMeta, LayoutRenderer } from '@/components/custom/layout-renderer'
import { PageHeader } from '@/components/layout/page-header'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { errorMessage } from '@/lib/error-message'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/m/$moduleKey/$entityKey/$recordId/')({
  component: RecordDetailPage,
})

function RecordDetailPage() {
  const { moduleKey, entityKey, recordId } = Route.useParams()
  const t = useTranslations()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const { data: module } = useQuery(
    orpc.modules.custom.modules.get.queryOptions({ input: { key: moduleKey } }),
  )

  const { data: entity, isLoading: entityLoading } = useQuery(
    orpc.modules.custom.entities.get.queryOptions({
      input: { moduleKey, key: entityKey },
    }),
  )

  const { data: record, isLoading: recordLoading } = useQuery(
    orpc.modules.custom.records.get.queryOptions({
      input: { moduleKey, entityKey, recordId },
    }),
  )

  const { data: layoutData } = useQuery(
    orpc.extensions.layouts.get.queryOptions({
      input: { moduleKey, entityKey, kind: 'detail' },
    }),
  )

  const deleteMutation = useMutation(
    orpc.modules.custom.records.delete.mutationOptions({
      onSuccess: () => {
        toast.success(t('entityRuntime.actions.delete'))
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.records.list.queryKey({
            input: { moduleKey, entityKey },
          }),
        })
        void navigate({
          to: '/m/$moduleKey/$entityKey',
          params: { moduleKey, entityKey },
        })
      },
      onError: (err) => toast.error(errorMessage(err, t)),
    }),
  )

  if (entityLoading || recordLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!entity || !record) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">Record not found</h1>
      </div>
    )
  }

  const title = computeRecordTitle(entity as { fields: FieldDefinition[]; name: string }, {
    data: record.data,
    id: recordId,
  })

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        breadcrumbs={[
          { label: module?.name ?? moduleKey, to: '/m/$moduleKey', params: { moduleKey } },
          {
            label: entity.namePlural ?? entity.name,
            to: '/m/$moduleKey/$entityKey',
            params: { moduleKey, entityKey },
          },
          { label: title },
        ]}
        title={title}
        actions={
          <>
            <Button asChild>
              <Link
                to="/m/$moduleKey/$entityKey/$recordId/edit"
                params={{ moduleKey, entityKey, recordId }}
              >
                <Pencil className="h-4 w-4 mr-2" />
                {t('entityRuntime.actions.edit')}
              </Link>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('entityRuntime.actions.openMenu')}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setConfirmOpen(true)}
                >
                  {t('entityRuntime.actions.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('entityRuntime.actions.delete')}</AlertDialogTitle>
                  <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deleteMutation.mutate({ moduleKey, entityKey, recordId })}
                    className="bg-destructive hover:bg-destructive/90"
                    disabled={deleteMutation.isPending}
                  >
                    {t('entityRuntime.actions.delete')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        }
      />

      {layoutData ? (
        <LayoutRenderer
          layout={layoutData.layout as unknown as LayoutManifest}
          record={record.data}
          coreFields={
            new Map<string, LayoutFieldMeta>(
              (entity.fields as FieldDefinition[]).map((f) => [
                f.key,
                {
                  label: f.label,
                  type: f.type,
                  options: f.options,
                  currency: f.currency,
                  allowOverride: f.allowOverride,
                },
              ]),
            )
          }
          extensionFields={new Map()}
          scenarioColumns={
            (entity.scenarios as { columns?: Array<{ key: string; label?: string }> } | null)
              ?.columns ?? undefined
          }
          onAction={(actionKey) => {
            toast.info(`Action dispatched: ${actionKey}`)
          }}
        />
      ) : (
        <EntityDetail
          fields={entity.fields as FieldDefinition[]}
          computedFields={entity.computedFields as ComputedFieldDefinition[]}
          data={record.data}
          audit={{
            createdAt: record.createdAt,
            createdByUserId: record.createdByUserId,
            updatedAt: record.updatedAt,
            updatedByUserId: record.updatedByUserId,
          }}
        />
      )}
    </div>
  )
}

function computeRecordTitle(
  entity: { fields: FieldDefinition[]; name: string },
  record: { data: Record<string, unknown>; id: string },
): string {
  const candidate = entity.fields.find(
    (f) => ['text', 'email'].includes(f.type) && record.data[f.key],
  )
  return candidate ? String(record.data[candidate.key]) : `${entity.name} ${record.id.slice(0, 8)}`
}
